import { useEffect } from 'react';
import { Provider, useSelector } from 'react-redux';
import { BrowserRouter, useLocation } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider, CssBaseline } from '@mui/material';
import { AxiosError } from 'axios';
import { store, RootState } from './store';
import { createAppTheme } from './theme';
import { PLATFORM_PRIMARY, PLATFORM_SECONDARY, resolveBrandColor } from './theme/brand';
import AppRoutes from './routes/AppRoutes';
import { I18nProvider, useI18n } from './i18n/I18nProvider';
import { RtlCacheProvider } from './i18n/RtlCacheProvider';
import { isNetworkError } from './utils/networkError';
import { BackendHealthProvider } from './hooks/useBackendHealth';
import DemoWriteBlockedToast from './components/demo/DemoWriteBlockedToast';
import AppErrorBoundary from './components/layout/AppErrorBoundary';
import { resolveAssetUrl } from './utils/assetUrl';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        if (isNetworkError(error)) return false;
        if (error instanceof AxiosError && error.response?.status && error.response.status < 500) {
          return false;
        }
        return failureCount < 1;
      },
      refetchOnWindowFocus: false,
      staleTime: 30000,
      networkMode: 'online',
    },
    mutations: {
      retry: false,
      networkMode: 'online',
    },
  },
});

/** Theme + Emotion RTL cache must sit inside I18nProvider so direction updates on language switch. */
function ThemedApp() {
  const themeMode = useSelector((state: RootState) => state.theme.mode);
  const tenant = useSelector((state: RootState) => state.tenant);
  const { dir } = useI18n();
  const location = useLocation();
  const theme = createAppTheme(
    themeMode,
    resolveBrandColor(tenant.branding.primaryColor, PLATFORM_PRIMARY),
    resolveBrandColor(tenant.branding.secondaryColor, PLATFORM_SECONDARY),
    dir,
  );

  useEffect(() => {
    const href = resolveAssetUrl(tenant.branding.favicon || tenant.branding.logo || '');
    if (!href) return;
    const ext = href.split('?')[0].split('.').pop()?.toLowerCase();
    const type = ext === 'ico'
      ? 'image/x-icon'
      : ext === 'svg'
        ? 'image/svg+xml'
        : ext === 'png'
          ? 'image/png'
          : ext === 'jpg' || ext === 'jpeg'
            ? 'image/jpeg'
            : undefined;
    document.querySelectorAll<HTMLLinkElement>("link[rel='icon'], link[rel='shortcut icon']")
      .forEach((el) => el.remove());
    const link = document.createElement('link');
    link.rel = 'icon';
    if (type) link.type = type;
    link.href = href;
    document.head.appendChild(link);
  }, [tenant.branding.favicon, tenant.branding.logo]);

  return (
    <RtlCacheProvider direction={dir}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <AppErrorBoundary resetKey={location.pathname}>
          <AppRoutes />
        </AppErrorBoundary>
        <DemoWriteBlockedToast />
      </ThemeProvider>
    </RtlCacheProvider>
  );
}

export default function App() {
  return (
    <Provider store={store}>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true,
          }}
        >
          <BackendHealthProvider>
            <I18nProvider>
              <ThemedApp />
            </I18nProvider>
          </BackendHealthProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </Provider>
  );
}
